import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useLeadListVariablesMutations from "../../../../datahooks/useLeadListVariablesMutations";
import {
  CreateVariableSchema,
  type CreateVariableSchemaType,
} from "../../../../schemas";

interface CreateVariableDialogProps {
  isOpen: boolean;
  onClose: () => void;
  listId: string;
}

export default function CreateVariableDialog({
  isOpen,
  onClose,
  listId,
}: CreateVariableDialogProps) {
  const isTabletOrDesktop = useTwBreakpoint("lg");
  const { createVariable, isCreatingVariable } =
    useLeadListVariablesMutations();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateVariableSchemaType>({
    defaultValues: { name: "" },
    resolver: valibotResolver(CreateVariableSchema),
  });

  //the endpoint takes a list of names, a single variable is a one entry payload
  function onCreate({ name }: CreateVariableSchemaType) {
    createVariable({ listId, variableNames: [name] }).then(() => onClose());
  }

  const Component = isTabletOrDesktop ? Dialog : Drawer;
  const Content = isTabletOrDesktop ? DialogContent : DrawerContent;
  const Header = isTabletOrDesktop ? DialogHeader : DrawerHeader;
  const Body = isTabletOrDesktop ? DialogBody : DrawerBody;
  const Footer = isTabletOrDesktop ? DialogFooter : DrawerFooter;

  return (
    <Component
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      onAfterClose={reset}
    >
      <Content>
        <form onSubmit={handleSubmit(onCreate)}>
          <Header title="Create custom variable" />

          <Body>
            <FormField
              label="Variable name"
              htmlFor="variableName"
              error={errors.name?.message}
            >
              <Input
                autoFocus
                id="variableName"
                placeholder="e.g. Phone number"
                variant={isTabletOrDesktop ? "md" : "lg"}
                {...register("name")}
              />
            </FormField>
          </Body>

          <Footer className="justify-end">
            <Button
              type="button"
              variant="secondary-black"
              size={isTabletOrDesktop ? "md" : "lg"}
              disabled={isCreatingVariable}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size={isTabletOrDesktop ? "md" : "lg"}
              isLoading={isCreatingVariable}
            >
              Create variable
            </Button>
          </Footer>
        </form>
      </Content>
    </Component>
  );
}
